import React, { useEffect, useState, useRef, useContext, useCallback } from "react";
import { DataContext } from "../../DataContext"; // Import the context
import "./SwapYourBike.scss";
import "../../pages/MyBykRent/HideLinks.scss";
import CrossIcon from "../../images/cross-mark-black.svg";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import Thumbnail from "../../images/swap-my-byk-desk.png";
import ThumbnailMobile from "../../images/image.jpg";
import MyBykStationPin from "../../images/mybyk-location-pin-small.svg";
import MyBykStationPinBig from "../../images/mybyk-location-pin-big.svg";
import {
  LoadScript,
  GoogleMap,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import API_BASE_URL from "../../config";
import { useLocation, useNavigate } from "react-router-dom";
import useCityContent from "../../hooks/useCityContent";
import InstagramLogo from "../../images/instagram-play.svg"
import YoutubeIcon from "../../images/youtube-red.svg"

const MapEnv = process.env.REACT_APP_MAPS_API;
// Static bearer token from environment variables
const bearerToken = process.env.REACT_APP_BEARER_TOKEN;

const libraries = ["places"];
const SwapYourBike = () => {
  const { cityData } = useCityContent();
  const { isValidParam, data } = useContext(DataContext);
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const location = useLocation();
  const serviceLat = data?.serviceLat;
  const serviceLong = data?.serviceLong;
  const [center, setCenter] = useState({ lat: parseFloat(serviceLat), lng: parseFloat(serviceLong) }); // Default coordinates
  const [zoom, setZoom] = useState(12); // Default zoom level
  const [mapHeight, setMapHeight] = useState("565px");
  const [activeMarker, setActiveMarker] = useState(null);
  //const [stationList, setStationList] = useState([]); // Dynamic station list
  const [isError, setIsError] = useState(false);
  const errorMessage = "No nearest hub data found";

  const queryParam = new URLSearchParams(location.search).get("q");
  useEffect(() => {
    if (!queryParam) return; // close if `q` is empty
    const updateMapHeight = () => {
      setMapHeight(window.innerWidth < 991 ? "232px" : "565px");
    };

    updateMapHeight();
    window.addEventListener("resize", updateMapHeight);

    return () => window.removeEventListener("resize", updateMapHeight);
  }, [queryParam]);

  useEffect(() => {
    // Bind FancyBox
    Fancybox.bind("[data-fancybox]", {
      Video: {
        autoplay: true,
      },
    });
    document.addEventListener("click", (event) => {
      if (event.target.closest('[data-fancybox="video-gallery"]')) {
        if (window.pauseYouTubeVideo) {
          window.pauseYouTubeVideo(); // Call the global function to pause YouTube videos
        }
      }
    });

    return () => Fancybox.destroy(); // Cleanup bindings
  }, []);

  const navigate = useNavigate();

  const hubdataurl = `${API_BASE_URL}WsClusterStationList.php`;
  const [mapData, setMapData] = useState([{ stationList: [] }]);

  const autocompleteInitialized = useRef(false);

useEffect(() => {
  const initializeAutocomplete = () => {
    if (window.google && inputRef.current && !autocompleteInitialized.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["geocode"],
        componentRestrictions: { country: "IN" },
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const selectedCenter = { lat, lng };

          setCenter(selectedCenter);
          setZoom(16);
          mapRef.current?.panTo(selectedCenter);

          const searchDataForMap = {
            dashboard: queryParam,
            action: "getstationlist",
            searchplace_lat: lat,
            searchplace_long: lng,
          };

          const fetchSearchData = async () => {
            try {
              const response = await fetch(hubdataurl, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `bearer ${bearerToken}`,
                },
                body: JSON.stringify(searchDataForMap),
              });

              const result = await response.json();
              setMapData(response.ok ? result.data : []);
            } catch {
              setMapData([]);
            }
          };
          fetchSearchData();
        }
      });

      autocompleteInitialized.current = true;
    }
  };

  if (window.google) {
    initializeAutocomplete();
  } else {
    const interval = setInterval(() => {
      if (window.google) {
        initializeAutocomplete();
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }
}, [queryParam, hubdataurl, navigate]);

useEffect(() => {
  if (!serviceLat || !serviceLong || !queryParam || !isValidParam) return;

  const newCenter = {
    lat: parseFloat(serviceLat),
    lng: parseFloat(serviceLong),
  };

  setCenter(newCenter);
  setZoom(12);
  mapRef.current?.panTo(newCenter);
}, [serviceLat, serviceLong, queryParam, isValidParam]);

  const fetchData = useCallback(async () => {
  try {
    const requestDataForMap = {
      dashboard: queryParam,
      action: 'getstationlist'
    };

    const response = await fetch(hubdataurl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `bearer ${bearerToken}`,
      },
      body: JSON.stringify(requestDataForMap),
    });

    const result = await response.json();

    if (response.ok) {
      setMapData(result.data); // Update data on success
    } else {
      setMapData([]); // API returned an error
    }
  } catch (error) {
    setMapData([]); // Network or other errors
  }
}, [hubdataurl, queryParam, setMapData]);

useEffect(() => {
  fetchData();
}, [fetchData]);

  // Log updated mapData whenever it changes
  useEffect(() => {
    //console.log("Updated mapData:", mapData);
  }, [mapData]);

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
  };

  const mapContainerStyle = {
    width: "100%",
    height: mapHeight, // Dynamically set height
  };

  const handleCloseError = () => {
    setIsError(false);
    inputRef.current.value = "";
  };
  const handleActiveMarker = (station) => {
    if (station.idStation === activeMarker) {
      // Close the InfoWindow if the same marker is clicked again
      setActiveMarker(null);
      return;
    }
    setActiveMarker(station.idStation); // Set active marker
    setCenter({
      lat: parseFloat(station.stationLat),
      lng: parseFloat(station.stationLong),
    });
    setZoom(16); // Zoom to focus
  };


  const isYouTube = cityData.swap_main_video?.includes("youtu");
  const isInstagram = cityData.swap_main_video?.includes("instagram");

  // Extract YouTube video ID
  const getYouTubeThumbnail = (url) => {
    const videoIdMatch = url.match(/(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/);
    return videoIdMatch ? `https://img.youtube.com/vi/${videoIdMatch[1]}/maxresdefault.jpg` : null;
  };
  const thumbnail = isYouTube ? getYouTubeThumbnail(cityData.swap_main_video) : null;


  return (
    <section className="swap-bike section-padding-sm">
      <div className="container">
        <div className="swap-bike__header section-header">
          <h2 dangerouslySetInnerHTML={{ __html: cityData.swap_header_title }}>
          </h2>
        </div>

        <div className="video">
          {isInstagram ? (
            <a href={cityData.swap_main_video} target="_blank" rel="noopener noreferrer" className=" video-thumbnail-ig-wrap" >
              <div className="video-icon">
                <img
                  src={InstagramLogo}
                  alt="play video icon"
                />
                <span>Watch on instagram</span>
              </div>
              <img
                src={cityData.swap_main_thumbnail}
                alt="Play Video"
                className="video-thumbnail video-thumbnail-ig "
              />
            </a>
          ) : (
            <a href={cityData.swap_main_video} data-fancybox="video-gallery">
              <div className="video-icon">
                <img
                  src={YoutubeIcon}
                  alt="play video icon"
                />
              </div>
              <img
                src={thumbnail || Thumbnail}
                alt="Play Video"
                className="video-thumbnail desk-image"
              />
              <img
                src={thumbnail || ThumbnailMobile}
                alt="Play Video"
                className="video-thumbnail mob-image"
              />
            </a>
          )}
        </div>

        <div className="paragraph">
          <p dangerouslySetInnerHTML={{ __html: cityData.swap_description }}>
          </p>

          {cityData.swap_sub_description && (
            <p
              dangerouslySetInnerHTML={{
                __html: cityData.swap_sub_description,
              }}
              className="sub-paragraph"
            ></p>
          )}
        </div>

        {isValidParam && (
          <div className="swap-bike__map">
            <div className="search-input-wrapper">
              <input
                type="text"
                ref={inputRef} // Attach the ref to the input field
                placeholder={cityData.map_input_placeholder}
                onChange={(e) => {
                  if (e.target.value.trim() === "") {
                    setZoom(12);
                    setCenter({lat: parseFloat(serviceLat), lng: parseFloat(serviceLong)});
                    fetchData();
                  }
                }}
              />
            </div>
            <div className="google-map-container">
              {isError && (
                <div className="error_msg-parent">
                  <div className="error_msg-c">
                    <button className="error-msg-b" onClick={handleCloseError}>
                      <img src={CrossIcon} alt="" />
                    </button>
                    <p>{errorMessage}</p>
                    <button className="error_msg-close">Dismiss</button>
                  </div>
                </div>
              )}
              <LoadScript googleMapsApiKey={MapEnv} libraries={libraries}>
                <GoogleMap
                  center={center}
                  zoom={zoom}
                  mapContainerStyle={mapContainerStyle}
                  options={mapOptions}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                >
                  {mapData.stationList?.filter((station) => station.stationLat && station.stationLong).map((station, index) => {
                    const isNearest =
                      center.lat === parseFloat(station.stationLat) &&
                      center.lng === parseFloat(station.stationLong);

                    const isActive = activeMarker === station.idStation;

                    let markerIcon = MyBykStationPin;

                    if (isActive) {
                      markerIcon = MyBykStationPinBig;
                    } else if (isNearest && !isActive) {
                      markerIcon = MyBykStationPin;
                    } else if (isNearest) {
                      markerIcon = MyBykStationPinBig;
                    }

                    return (
                      <Marker
                        key={station.idStation || `marker-${index}`}
                        position={{
                          lat: parseFloat(station.stationLat),
                          lng: parseFloat(station.stationLong),
                        }}
                        title={station.stationName}
                        icon={markerIcon}
                        onClick={() => handleActiveMarker(station)} // Set active marker on click


                      />
                    );
                  })}
                  {/* {activeMarker && (
                    <InfoWindow
                      key={`info-window-${activeMarker}`}
                      position={{
                        lat: parseFloat(
                          mapData.stationList.find(
                            (station) => station.idStation === activeMarker
                          ).stationLat
                        ),
                        lng: parseFloat(
                          mapData.stationList.find(
                            (station) => station.idStation === activeMarker
                          ).stationLong
                        ),
                      }}
                      onCloseClick={() => setActiveMarker(null)} // Close the InfoWindow
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -60), // Adjust as needed to reposition the InfoWindow
                      }}
                    >
                      <div>
                        <h4>
                          {
                            mapData.stationList.find(
                              (station) => station.idStation === activeMarker
                            ).stationName
                          }
                        </h4>
                      </div>
                    </InfoWindow>
                  )} */}
                  {activeMarker && (
                    <InfoWindow
                      key={`info-window-${activeMarker}`}
                      position={{
                        lat: parseFloat(
                          mapData.stationList.find(
                            (station) => station.idStation === activeMarker
                          ).stationLat
                        ),
                        lng: parseFloat(
                          mapData.stationList.find(
                            (station) => station.idStation === activeMarker
                          ).stationLong
                        ),
                      }}
                      onCloseClick={() => setActiveMarker(null)} // Close the InfoWindow
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -60), // Adjust as needed to reposition the InfoWindow
                      }}
                    >
                      <div>
                        <h4>
                          {
                            mapData.stationList.find(
                              (station) => station.idStation === activeMarker
                            ).stationName
                          }
                        </h4>
                      </div>
                    </InfoWindow>
                  )}

                </GoogleMap>
              </LoadScript>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SwapYourBike;
